<template>
  <PVSelect
    v-model="model"
    :options="options"
    :pt="passThroughProps"
    :auto-filter-focus="autoFilterFocus"
    :title="controlTitle"
    unstyled
    option-label="text"
    option-value="value"
    scroll-height="19rem"
    @change="onChange"
    @before-show="$emit('before-show')"
    @show="$emit('show')"
    @before-hide="$emit('before-hide')"
    @hide="$emit('hide')"
  >
    <template #header>
      <slot name="header" />
    </template>
    <template #dropdownicon>
      <span class="mimic-bv-caret" />
    </template>
    <template
      v-if="slots.option"
      #option="slotProps"
    >
      <slot
        name="option"
        v-bind="slotProps"
      />
    </template>
    <template #emptyfilter>
      <li role="presentation">
        <p class="b-dropdown-text">{{ translate({ path: 'VIRTUAL_LIST.NO_RESULTS' }) }}</p>
      </li>
    </template>
  </PVSelect>
</template>
<script lang="ts">
export default {
  name: 'StandardSelect',
  compatConfig: {
    MODE: 3,
  },
};
</script>
<script setup lang="ts" generic="T">
import { computed, onUnmounted, useSlots } from 'vue';
import type { SelectContext, SelectPassThroughOptions } from 'primevue/select';
import { PassThrough } from '@primevue/core';
import useTranslation from '@/composables/useTranslation';

const props = withDefaults(
  defineProps<{
    options: { value: T; text: string }[];
    pt?: Record<string, any>;
    size?: 'sm' | 'md' | 'lg';
    autoFilterFocus?: boolean;
  }>(),
  {
    size: 'md',
    autoFilterFocus: true,
  },
);

defineEmits<{
  (e: 'before-show'): void;
  (e: 'show'): void;
  (e: 'before-hide'): void;
  (e: 'hide'): void;
}>();

const model = defineModel<T>({ required: true });

const sizeClass = computed(() => {
  if (props.size === 'sm') {
    return 'form-control-sm';
  }

  if (props.size === 'lg') {
    return 'form-control-lg';
  }

  return '';
});

const slots = useSlots();

const passThroughProps = computed(
  (): PassThrough<SelectPassThroughOptions> => ({
    root: {
      class: [
        'form-control',
        sizeClass.value,
        'd-inline-flex',
        'align-items-center',
        'w-100', // take parent width (col-sm-6)
        'mw-100', // don't exceed parent
        'text-center',
        'cursor-pointer',
      ]
        .filter(Boolean)
        .join(' '),
    },
    label: {
      class: 'flex-grow-1 text-ellipsis',
      style: 'min-width: 0', // required for flex truncation
    },
    dropdown: {
      class: 'flex-shrink-0',
    },
    overlay: {
      class: [
        'dropdown-menu',
        'dropdown-menu-compact',
        'py-0',
        'border',
        'rounded',
        'shadow',
        'show',
        ...(props.pt?.overlay?.class ?? []),
      ]
        .filter(Boolean)
        .join(' '),
      style: 'max-width: 200px',
    },
    header: {
      class: 'px-3',
    },
    pcFilterContainer: {
      root: {
        class: 'form-group row py-2 mb-0',
      },
    },
    pcFilter: {
      root: {
        class: 'col form-control form-control-sm mx-3',
      },
    },
    pcFilterIconContainer: {
      root: {
        class: 'd-none',
      },
    },
    listContainer: {
      class: 'scroll-md',
    },
    list: {
      class: 'pl-0 mb-0',
    },
    option: ({ context }: { context: SelectContext }) => ({
      class: [
        'cursor-pointer',
        'text-ellipsis',
        'overflow-hidden',
        {
          'dropdown-item': !slots.option,
          active: context?.selected,
          'font-size-sm': props.size === 'sm',
        },
      ],
    }),
    optionLabel: {
      class: 'd-block text-ellipsis overflow-hidden',
      style: 'min-width: 0', // required for flex truncation
    },
  }),
);

const controlTitle = computed(() => props.options.find((option) => option.value === model.value)?.text);

const { translate } = useTranslation();

let removeSwallow: (() => void) | null = null;

/**
 * Prevents the parent component from closing when the select or select option is clicked.
 */
const swallowParentClickOnce = () => {
  removeSwallow?.();

  const onClick = (event: MouseEvent) => {
    event.stopPropagation();
    removeSwallow?.();
  };

  document.addEventListener('click', onClick, true);
  removeSwallow = () => {
    document.removeEventListener('click', onClick, true);
    removeSwallow = null;
  };
};

const onChange = (event: { originalEvent?: Event }) => {
  // Keyboard select has no follow-up click; don't leave a trap for the next click.
  if (event.originalEvent?.type !== 'mousedown') return;

  swallowParentClickOnce();
};

onUnmounted(() => {
  removeSwallow?.();
});
</script>
